import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  Button,
  Link
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BackupIcon from '@mui/icons-material/Backup';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LaunchIcon from '@mui/icons-material/Launch';
import LockIcon from '@mui/icons-material/Lock';
import TimelineIcon from '@mui/icons-material/Timeline';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Paper from '@mui/material/Paper';

// Simple feature badge component
const FeatureBadge: React.FC<{ label: string; variant?: 'primary' | 'warning' | 'secondary' }> = ({ 
  label, 
  variant = 'primary' 
}) => (
  <Chip
    label={label}
    size="small"
    color={variant}
    variant="outlined"
    sx={{
      fontSize: '0.65rem',
      height: 18,
      ml: 1,
      '& .MuiChip-label': { px: 0.8 }
    }}
  />
);

const ActionsTab: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Introduction Box with AppConfig² Only Notice */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FlashOnIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 0 }}>
              Management Actions
            </Typography>
            <FeatureBadge label="AppConfig² Only" />
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Execute <strong>powerful administrative actions</strong> to manage your Microsoft Entra ID environment efficiently and securely.
          These management capabilities are available in AppConfig² to provide complete application lifecycle control.
        </Typography>
        <Typography variant="body2" color="text.primary">
          AppTesting provides comprehensive analysis and testing in read-only mode. For management operations, 
          these actions are available in AppConfig² with built-in safeguards, validation steps, and comprehensive documentation.
        </Typography>
      </Paper>

      {/* AppConfig² Only Notice */}
      <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
        <AlertTitle>AppConfig² Management Features</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The actions below require AppConfig² for full management capabilities. AppTesting focuses on 
          analysis, testing, and validation in a secure read-only environment.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon />}
            href="https://appconfigweb.thetrask.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn About AppConfig²
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon />}
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal (Alternative)
          </Button>
        </Box>
      </Alert>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Available Actions in AppConfig²
      </Typography>

      {/* Enhanced Action Cards Grid with AppConfig² Only styling */}
      <Grid container spacing={3}>
        {/* Register New Application */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, opacity: 0.85, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AddCircleOutlineIcon color="primary" sx={{ mr: 1, color: '#4caf50' }} />
                <Typography variant="h6">Register New Application</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Create a new application registration in your Microsoft Entra ID tenant with guided configuration. This action helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Configure application type (SPA, Web, API, etc.)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Set up initial redirect URIs and configurations" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Create both application registration and service principal" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Provide next steps for further configuration" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Single-Page Apps" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Web Applications" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="APIs" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                <Chip size="small" label="Service Principal" sx={{ bgcolor: '#fce4ec', color: '#c2185b' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Application Lifecycle Viewer */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, opacity: 0.85, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon color="primary" sx={{ mr: 1, color: '#7c3aed' }} />
                <Typography variant="h6">Application Lifecycle Viewer</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Track and visualize the complete lifecycle of your applications from creation to retirement. This tool helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• View creation timeline and ownership history" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Track service principal creation and status" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Monitor ownership assignments and changes" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Generate lifecycle metrics and dashboards" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Timeline Tracking" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} />
                <Chip size="small" label="Ownership History" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="Lifecycle Metrics" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Compliance Audit" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Restore Deleted Applications */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, opacity: 0.85, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RestoreIcon color="primary" sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="h6">Restore Deleted Applications</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Restore accidentally deleted applications within 30 days or permanently clean up expired deletions. This action helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• View all deleted applications in your tenant" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Restore app registration and service principal together" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Search and filter deleted applications" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Permanently delete expired applications" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="30-Day Window" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                <Chip size="small" label="Full Restoration" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="Smart Search" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Cleanup Tools" sx={{ bgcolor: '#fce4ec', color: '#c2185b' }} />
              </Box>
              <Box sx={{ mt: 2, bgcolor: '#fff8e1', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Restoration Note:</strong> Matches Azure portal behavior by restoring both application registration 
                  and service principal, maintaining enterprise application functionality.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Backup Applications */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, opacity: 0.85, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BackupIcon color="primary" sx={{ mr: 1, color: '#2196f3' }} />
                <Typography variant="h6">Backup Applications</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Export configurations of all your Entra ID applications for backup and documentation purposes. This action helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Export complete application configurations as JSON" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Include redirect URIs, permissions, and settings" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Create timestamped backup files for versioning" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Maintain documentation of your environment" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="JSON Export" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                <Chip size="small" label="Full Configuration" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                <Chip size="small" label="Disaster Recovery" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Delete Application */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, opacity: 0.85, position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DeleteForeverIcon color="primary" sx={{ mr: 1, color: '#f44336' }} />
                <Typography variant="h6">Delete Application</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography paragraph variant="body2" color="text.secondary">
                Permanently remove an application registration from Azure AD with comprehensive safety measures. This action helps you:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="• Manual Client ID entry for additional security" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Risk assessment and safety confirmations" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Multi-layer confirmation process" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="• Complete removal of app registration and service principal" />
                </ListItem>
              </List>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Manual Entry Required" sx={{ bgcolor: '#ffebee', color: '#c62828' }} />
                <Chip size="small" label="Risk Assessment" sx={{ bgcolor: '#fff3e0', color: '#ef6c00' }} />
                <Chip size="small" label="Safety Confirmations" sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
              </Box>
              <Box sx={{ mt: 2, bgcolor: '#fff3e0', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Security Note:</strong> This action requires manual Client ID entry and multiple confirmations to prevent accidental deletions.
                  Remember: deleted applications can be restored within 30 days.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alternative Actions for AppTesting Users */}
      <Card sx={{ mt: 4, borderRadius: 2, bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LockIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6" color="warning.dark">
              Management Alternatives for AppTesting Users
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            While AppTesting provides comprehensive analysis capabilities in read-only mode, you can perform management 
            actions using these alternative approaches:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Azure Portal
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Use the Azure Portal for all management operations including registration, deletion, and configuration changes.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon />}
                  href="https://portal.azure.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  Open Azure Portal
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                  Upgrade to AppConfig²
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Get full management capabilities integrated with comprehensive analysis tools in one platform.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon />}
                  href="https://appconfigapp.toncheetah.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="success"
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* AppConfig² Features Overview - Updated with Lifecycle Viewer */}
      <Card sx={{ mt: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>AppConfig² Action Workflows</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The following workflows are available in AppConfig² with comprehensive safety measures and guided processes.
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AddCircleOutlineIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Application Registration
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The registration process guides you through creating a new application with the correct configuration
                  for your specific use case, ensuring all necessary components are properly set up.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Application Type Selection" 
                      secondary="Choose between SPA, Web, API, or hybrid configurations" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Service Principal Creation" 
                      secondary="Automatically creates the associated service principal" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Configuration Guidance" 
                      secondary="Provides next steps for completing the setup" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TimelineIcon sx={{ mr: 1, color: '#7c3aed' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Lifecycle Management
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The lifecycle viewer provides comprehensive tracking of application history, ownership changes,
                  and important events throughout the application's lifetime in your tenant.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Creation Timeline" 
                      secondary="Track when apps and service principals were created" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Ownership Tracking" 
                      secondary="Monitor owner assignments and management history" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Dashboard Metrics" 
                      secondary="Generate insights on app age, ownership, and patterns" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2.4}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <RestoreIcon sx={{ mr: 1, color: '#ff9800' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Application Restoration
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The restoration process comprehensively recovers deleted applications, matching Azure portal behavior
                  by restoring both the application registration and service principal components.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Smart Discovery" 
                      secondary="Automatically finds and lists all restorable applications" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Dual Restoration" 
                      secondary="Restores both app registration and service principal" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Validation & Cleanup" 
                      secondary="Verifies restoration success and provides cleanup options" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2.4}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BackupIcon sx={{ mr: 1, color: '#2196f3' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Backup Process
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The backup process creates comprehensive exports of your application configurations,
                  ensuring you have complete documentation and recovery options for your environment.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Complete Configuration Export" 
                      secondary="Includes all settings, URIs, permissions, and metadata" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Timestamped Files" 
                      secondary="Each backup is clearly labeled with creation timestamp" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="JSON Format" 
                      secondary="Standard format for easy import and version control" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2.4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DeleteForeverIcon sx={{ mr: 1, color: '#f44336' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Secure Deletion Process
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The deletion process includes multiple safety measures to prevent accidental removal
                  of critical applications while ensuring complete cleanup when deletion is intended.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Manual Client ID Entry" 
                      secondary="Prevents accidental selection from lists" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Risk Assessment" 
                      secondary="Automatically evaluates potential impact of deletion" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Multi-layer Confirmations" 
                      secondary="Multiple checkboxes and final confirmation dialog" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Best Practices Section - Updated with Lifecycle Management */}
      <Card sx={{ mt: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Management Best Practices (AppConfig²)</Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(124, 58, 237, 0.1)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#7c3aed' }}>
                  Lifecycle Management Best Practices
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Regular lifecycle reviews"
                      secondary="Use the lifecycle viewer to audit app ages and ownership quarterly"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Track ownership assignments"
                      secondary="Ensure all applications have designated owners for accountability"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Monitor application metrics"
                      secondary="Use dashboard insights to identify patterns and optimization opportunities"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Document lifecycle events"
                      secondary="Maintain records of important changes for compliance and auditing"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2e7d32' }}>
                  Registration & Restoration Best Practices
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Use descriptive names"
                      secondary="Choose clear, meaningful names that indicate the application's purpose"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Create backups before major changes"
                      secondary="Always backup applications before significant modifications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Verify restorations immediately"
                      secondary="Test restored applications to ensure all functionality works correctly"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Monitor 30-day deletion window"
                      secondary="Track deleted applications to prevent permanent loss of important apps"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Backup & Maintenance
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Regular backups"
                      secondary="Create backups before making significant changes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Version control"
                      secondary="Store backup files in version control systems"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Document changes"
                      secondary="Maintain change logs alongside backup files"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Clean up expired deletions"
                      secondary="Regularly review and permanently delete expired applications"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Application Lifecycle Management in AppConfig²
            </Typography>
            <Typography variant="body2">
              AppConfig² provides comprehensive application lifecycle management from creation to deletion and restoration. 
              The Application Lifecycle Viewer offers detailed tracking of ownership changes, creation timelines, and important
              events throughout each application's lifetime. Use this data for compliance reporting, governance decisions, and
              proactive environment management.
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Important Security Considerations
            </Typography>
            <Typography variant="body2">
              Always create backups before performing destructive actions. The deletion process includes multiple safeguards,
              but deleted applications can only be recovered within 30 days. Use the Lifecycle Viewer to understand ownership
              and dependencies before proceeding with any deletion operations. Ensure you understand the impact on dependent 
              services and maintain proper documentation of lifecycle events for audit purposes.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActionsTab;